import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiSend } from '@/lib/api';
import { PageHeader, Card, Spinner, EmptyState, ErrorNote } from '@/components/ui';
import { askFlightTime } from '@/lib/flightPatch';

interface LivePosition {
  registration: string;
  ident: string | null;
  airborne: boolean;
  origin: string | null;
  destination: string | null;
  progress: number | null;
}
interface Detected {
  id: string;
  flightaware_id: string;
  departure_airport: string;
  arrival_airport: string;
  departure_time: string;
  flight_time_hours?: number | string | null;
}

export default function AircraftTracking() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<{ tailNumber: string; current_flight: LivePosition | null } | null>(null);
  const [detected, setDetected] = useState<Detected[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);

  function load() {
    Promise.all([
      apiGet<{ tailNumber: string; current_flight: LivePosition | null }>('/api/flightaware/status'),
      apiGet<{ flights: Detected[] }>('/api/flightaware/unmatched'),
    ])
      .then(([s, u]) => {
        setStatus(s);
        setDetected(u.flights || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function sync() {
    setSyncing(true);
    try { await apiSend('/api/flightaware/sync', 'POST'); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Sync failed'); }
    finally { setSyncing(false); }
  }

  function logIt(d: Detected) {
    // Prefill the flight form; mark the detection logged once saved.
    navigate('/flights/new', {
      state: {
        prefill: {
          date: d.departure_time,
          departure_airport: d.departure_airport,
          arrival_airport: d.arrival_airport,
          // FlightAware's measured airborne time — the form's prefill, nothing more.
          airborne_hours: d.flight_time_hours != null ? Number(d.flight_time_hours) : undefined,
          flightaware_id: d.flightaware_id,
        },
        detectionId: d.id,
      },
    });
  }
  async function flagOther(d: Detected) {
    const flight_hours = askFlightTime(d.flight_time_hours);
    if (flight_hours === undefined) return;
    try { await apiSend(`/api/flightaware/detected/${d.id}/other`, 'POST', { flight_hours }); setDetected((list) => list.filter((x) => x.id !== d.id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to flag'); }
  }
  async function dismiss(id: string) {
    try { await apiSend(`/api/flightaware/detected/${id}/dismiss`, 'POST'); setDetected((d) => d.filter((x) => x.id !== id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Dismiss failed'); }
  }
  // Retire, don't delete. Deleting the row takes the flightaware_id with it, and
  // that id is the only reason the next sync skips a detection it has seen — so a
  // deleted detection reappears the next morning. See the route's comment.
  async function retire(id: string) {
    try { await apiSend(`/api/flightaware/detected/${id}/retire`, 'POST'); setDetected((d) => d.filter((x) => x.id !== id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Retire failed'); }
  }

  const current = status?.current_flight;
  const configured = Boolean(status); // status endpoint returns even w/o key; airborne implies live
  const airborne = current?.airborne;

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Aircraft Tracking"
        subtitle="N313G flight history via FlightAware — synced daily"
        actions={<button onClick={sync} disabled={syncing} className="btn-ghost text-sm">{syncing ? 'Syncing…' : 'Sync now'}</button>}
      />

      {error && <div className="mb-4"><ErrorNote message={error} /></div>}

      {loading ? (
        <Spinner />
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Status */}
          <Card className="p-6 lg:col-span-1">
            <div className="flex items-center justify-between">
              <span className="ident text-lg text-white">{status?.tailNumber || 'N313G'}</span>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${airborne ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-500/15 text-slate-400'}`}>
                <span className={`h-2 w-2 rounded-full ${airborne ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                {airborne ? 'In flight' : 'On ground'}
              </span>
            </div>
            {current ? (
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Last / current</dt><dd className="ident text-slate-200">{current.origin || '—'} → {current.destination || '—'}</dd></div>
                {airborne && current.progress != null && (
                  <div className="flex justify-between"><dt className="text-slate-500">Progress</dt><dd className="tabular-nums text-slate-200">{current.progress}%</dd></div>
                )}
              </dl>
            ) : (
              <p className="mt-4 text-sm text-slate-500">
                {configured ? 'No recent flights.' : (
                  <>No data. Add a <span className="ident text-xs text-slate-300">FLIGHTAWARE_API_KEY</span> to enable FlightAware history.</>
                )}
              </p>
            )}
          </Card>

          {/* Detected flights */}
          <Card className="p-6 lg:col-span-2">
            <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-300">Detected flights</h2>
            <p className="mb-4 text-xs text-slate-500">
              Flights FlightAware saw that aren't in your logbook yet. Log yours, or flag the
              co-owner's as <span className="text-slate-300">Other owner</span> so the aircraft's
              history still has them. The hours shown are FlightAware's airborne time, offered as
              the flight time when you log a leg.
            </p>
            {detected.length === 0 ? (
              <EmptyState title="Nothing to classify" hint="New detected flights appear here after the daily sync." />
            ) : (
              <ul className="divide-y divide-white/5">
                {detected.map((d) => (
                  <li key={d.id} className="-mx-2 flex flex-col gap-2 rounded-lg px-2 py-3 transition hover:bg-white/[0.03] sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="ident text-white">{d.departure_airport} → {d.arrival_airport}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(d.departure_time).toLocaleString()}
                        {d.flight_time_hours != null && Number(d.flight_time_hours) > 0 && ` · ${Number(d.flight_time_hours).toFixed(2)} h airborne`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => logIt(d)} className="btn-primary text-xs">Log it</button>
                      <button onClick={() => flagOther(d)} className="btn-ghost text-xs" title="The co-owner's flight — recorded in the aircraft's history, not your logbook">Other owner</button>
                      <button onClick={() => dismiss(d.id)} className="btn-ghost text-xs text-slate-500" title="Aircraft time that isn't anyone's logbook entry — kept in Aircraft History">Ignore</button>
                      <button onClick={() => retire(d.id)} className="btn-ghost text-xs text-slate-500" title="This never happened — a filed flight plan, not a flight. Removed from history and never detected again.">Not a flight</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
